import { App, Form, Modal } from 'antd';
import { useMemo } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { SegmentHistory } from 'business/quality-control/pages/segment-history/hooks/data/types';
import {
  useRingV1ControllerGetRings,
  useSegmentHistoryV1ControllerCreateManualSegmentHistory,
} from 'generated/apiComponents';
import {
  Module_Enum,
  useGetSegmentHistoryOptionsQuery,
} from 'generated/graphql';
import { isUndefinedOrNull } from 'technical/utils/is-null-or-undefined';

import { AddManualScanForm } from './add-manual-scan-form';
import { AddManualScanFormValues, ERROR_MAPPING } from './types';

export const AddManualScanModal = ({
  isModalVisible,
  closeModal,
  segment,
  refetchData,
}: {
  isModalVisible: boolean;
  closeModal: () => void;
  segment: SegmentHistory | null;
  refetchData: () => void;
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<AddManualScanFormValues>();

  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No constructionSite');

  const { mutate: addSegmentHistory } =
    useSegmentHistoryV1ControllerCreateManualSegmentHistory();

  const save = async (row: AddManualScanFormValues) => {
    addSegmentHistory(
      {
        body: {
          segmentId: segment?.id ?? '',
          status: row.status,
          zone: row.zone,
          ringId: row.ringId,
          date: row.date.utc().toISOString(),
        },
      },
      {
        onSuccess: async () => {
          refetchData();
          closeModal();

          message.open({
            type: 'success',
            content: t(
              'qualityControl.segmentHistory.addManualScanModal.success',
            ),
          });
        },
        onError: (error) => {
          const errorMessage = (error as any)?.message;
          const translationSuffix = ERROR_MAPPING[errorMessage];
          if (translationSuffix) {
            form.setFields([
              {
                name: 'status',
                errors: [
                  t(
                    `qualityControl.segmentHistory.addManualScanModal.errors.${translationSuffix}`,
                  ),
                ],
              },
            ]);
            return;
          }

          message.open({
            type: 'error',
            content: t(
              'qualityControl.segmentHistory.addManualScanModal.errors.technicalError',
            ),
          });
        },
      },
    );
  };

  const graphqlHeaders = useGraphqlApiHeaders(Module_Enum.QualityScan);
  const { data, loading } = useGetSegmentHistoryOptionsQuery({
    variables: {
      constructionSiteId: currentConstructionSite.id,
      projectId: currentConstructionSite.projectId,
    },
    context: { headers: graphqlHeaders },
  });
  const zoneOptions = useMemo(() => {
    const options =
      data?.scanSites?.map((site) => ({
        label: site.label,
        value: site.id,
        isReceipt: site.isReceiptActivated,
      })) || [];

    return options;
  }, [data]);

  const { data: ringsData, isLoading: ringsLoading } =
    useRingV1ControllerGetRings({
      pathParams: { constructionSiteId: currentConstructionSite.id },
    });
  const ringOptions = useMemo(() => {
    const options =
      ringsData?.rings?.map((ring) => ({
        label: ring.number,
        value: ring.id,
      })) || [];

    return options.sort((a, b) => {
      return b.label - a.label;
    });
  }, [ringsData]);

  return (
    <Modal
      title={t('qualityControl.segmentHistory.addManualScanModal.title')}
      open={isModalVisible}
      onOk={form.submit}
      onCancel={closeModal}
      destroyOnHidden
    >
      {!isUndefinedOrNull(segment) ? (
        <AddManualScanForm
          form={form}
          save={save}
          zoneOptions={zoneOptions}
          ringOptions={ringOptions}
          loading={loading || ringsLoading}
        />
      ) : null}
    </Modal>
  );
};
